

import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table'

import { cn } from '@/lib/utils'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  Columns,
  Download,
  FileText,
  Search,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

export interface ColumnConfig<T = unknown> {
  key: keyof T | string
  header: string
  accessor?: (row: T) => unknown
  cell?: (value: unknown, row: T) => ReactNode
  sortable?: boolean
  filterable?: boolean
  frozen?: boolean
  width?: number | string
  align?: 'left' | 'center' | 'right'
  type?: 'text' | 'number' | 'date' | 'boolean' | 'currency' | 'custom'
  format?: (value: unknown) => string
}

export interface ExportOptions {
  filename?: string
  title?: string
  includeFilters?: boolean
  customFields?: Record<string, unknown>
}

export interface PaginationCallbacks {
  onPageChange?: (pageIndex: number, pageSize: number) => void
  onPageSizeChange?: (pageSize: number) => void
  totalCount?: number
  manualPagination?: boolean
}

export interface EnhancedDataTableProps<T> {
  data: T[],
  enablePDFExport?: boolean
  columns: ColumnConfig<T>[]
  customButtons?: ReactNode
  title?: string
  description?: string
  searchPlaceholder?: string
  enableGlobalFilter?: boolean
  enableColumnFilters?: boolean
  enableExport?: boolean
  enableColumnVisibility?: boolean
  enablePagination?: boolean
  enableSorting?: boolean
  enableRowSelection?: boolean
  pageSize?: number
  pageSizeOptions?: number[]
  exportOptions?: ExportOptions
  className?: string
  onRowClick?: (row: T) => void
  loading?: boolean
  emptyStateMessage?: string
  frozenColumnsCount?: number
  paginationCallbacks?: PaginationCallbacks
  globalFilter?: string,
  setGlobalFilterFn?: (value: string) => void
  initialColumnVisibility?: VisibilityState
  onSelectedRowsChange?: (rows: T[]) => void
  clearSelectionTrigger?: number
}

function SortableHeader({
  column,
  children,
  align = 'left'
}: {
  column: { getCanSort: () => boolean; toggleSorting: (desc?: boolean) => void; getIsSorted: () => false | 'asc' | 'desc' }
  children: ReactNode
  align?: 'left' | 'center' | 'right'
}) {
  if (!column.getCanSort()) {
    return (
      <div className={cn('flex items-center space-x-2',
        align === 'left' ? 'justify-start' :
          align === 'center' ? 'justify-center' : 'justify-end'
      )}>
        {children}
      </div>
    )
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn('h-8 p-0 hover:bg-transparent',
        align === 'left' ? 'justify-start' :
          align === 'center' ? 'justify-center' : 'justify-end'
      )}
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      <div className="flex items-center space-x-2">
        <span>{children}</span>
        {column.getIsSorted() === 'desc' ? (
          <ArrowDown className="h-4 w-4" />
        ) : column.getIsSorted() === 'asc' ? (
          <ArrowUp className="h-4 w-4" />
        ) : (
          <ArrowUpDown className="h-4 w-4" />
        )}
      </div>
    </Button>
  )
}

export function EnhancedDataTable<T>({
  data,
  columns,
  title,
  description,
  searchPlaceholder = 'Search...',
  enableGlobalFilter = true,
  enableColumnFilters = true,
  enablePDFExport = false,
  enableExport = false,
  enableColumnVisibility = true,
  enablePagination = true,
  enableSorting = true,
  enableRowSelection = false,
  pageSize = 10,
  pageSizeOptions = [10, 20, 30, 40, 50, 100],
  exportOptions = {},
  className,
  onRowClick,
  loading = false,
  customButtons,
  emptyStateMessage = 'No data available.',
  frozenColumnsCount = 0,
  paginationCallbacks,
  globalFilter = '',
  setGlobalFilterFn,
  initialColumnVisibility = {},
  onSelectedRowsChange,
  clearSelectionTrigger,
}: EnhancedDataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialColumnVisibility)
  const [rowSelection, setRowSelection] = useState({})
  // const [globalFilter, setGlobalFilter] = useState('')
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  })
  const [internalGlobalFilter, setInternalGlobalFilter] = useState(globalFilter)

  const isControlledGlobalFilter = typeof setGlobalFilterFn === 'function'
  const currentGlobalFilter = isControlledGlobalFilter ? globalFilter : internalGlobalFilter

  const handleGlobalFilterChange = (value: string) => {
    if (isControlledGlobalFilter) {
      setGlobalFilterFn?.(value)
    } else {
      setInternalGlobalFilter(value)
    }
  }

  const handleTableGlobalFilterChange = (
    updater: string | ((previous: string) => string),
  ) => {
    const nextValue = typeof updater === 'function' ? updater(currentGlobalFilter) : updater
    handleGlobalFilterChange(nextValue)
  }

  // Convert ColumnConfig to TanStack ColumnDef
  const tableColumns = useMemo<ColumnDef<T>[]>(() => {
    const cols: ColumnDef<T>[] = []

    // Add selection column if enabled
    if (enableRowSelection) {
      cols.push({
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            title="Select all rows"
            checked={table.getIsAllPageRowsSelected()}
            onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
            className="rounded border border-input"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            title="Select row"
            checked={row.getIsSelected()}
            onChange={(e) => row.toggleSelected(e.target.checked)}
            className="rounded border border-input"
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40,
      })
    }

    // Add data columns
    columns.forEach((col, index) => {
      const isDefFrozen = index < frozenColumnsCount

      cols.push({
        id: col.key as string,
        accessorKey: col.key as string,
        accessorFn: col.accessor || ((row: T) => {
          const record = row as Record<string, unknown>
          return record[col.key as string]
        }),
        header: ({ column }) => (
          <SortableHeader column={column} align={col.align}>
            {col.header}
          </SortableHeader>
        ),
        cell: ({ getValue, row }) => {
          const value = getValue()

          if (col.cell) {
            return col.cell(value, row.original)
          }

          if (col.format) {
            return col.format(value)
          }

          // Default formatting based on type
          switch (col.type) {
            case 'currency':
              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
              }).format(Number(value) || 0)
            case 'date':
              return value ? new Date(value as string | number | Date).toLocaleDateString() : ''
            case 'boolean':
              return value ? (
                <Badge variant="default">Yes</Badge>
              ) : (
                <Badge variant="secondary">No</Badge>
              )
            case 'number':
              return new Intl.NumberFormat().format(Number(value) || 0)
            default:
              return value?.toString() || ''
          }
        },
        enableSorting: enableSorting && (col.sortable !== false),
        enableColumnFilter: enableColumnFilters && (col.filterable !== false),
        size: typeof col.width === 'number' ? col.width : undefined,
        meta: {
          align: col.align,
          frozen: isDefFrozen,
          width: col.width,
        },
      })
    })

    return cols
  }, [columns, enableRowSelection, enableSorting, enableColumnFilters, frozenColumnsCount])

  const table = useReactTable({
    data,
    columns: tableColumns,
    pageCount: paginationCallbacks?.manualPagination ?
      Math.ceil((paginationCallbacks?.totalCount || 0) / pagination.pageSize) :
      undefined,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter: currentGlobalFilter,
      pagination,
    },
    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: handleTableGlobalFilterChange,
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater
      setPagination(newPagination)

      // Call refetch callback when pagination changes
      if (paginationCallbacks?.onPageChange &&
        (newPagination.pageIndex !== pagination.pageIndex ||
          newPagination.pageSize !== pagination.pageSize)) {
        paginationCallbacks.onPageChange(newPagination.pageIndex, newPagination.pageSize)
      }

      if (paginationCallbacks?.onPageSizeChange &&
        newPagination.pageSize !== pagination.pageSize) {
        paginationCallbacks.onPageSizeChange(newPagination.pageSize)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: 'includesString',
    manualPagination: paginationCallbacks?.manualPagination || false,
  })

   // Check if any filters are active
  const hasActiveFilters = currentGlobalFilter.length > 0 || columnFilters.length > 0
  const hasNoRows = !loading && table.getRowModel().rows?.length === 0
  const emptyMessage = hasNoRows 
    ? (hasActiveFilters ? 'No data matches your search or filters. Try adjusting your criteria.' : emptyStateMessage)
    : ''

  useEffect(() => {
    if (!onSelectedRowsChange) return
    onSelectedRowsChange(table.getSelectedRowModel().rows.map(row => row.original))
  }, [onSelectedRowsChange, table, rowSelection, data])

  useEffect(() => {
    if (clearSelectionTrigger === undefined) return
    setRowSelection({})
  }, [clearSelectionTrigger])

  // Export functions
  const exportToExcel = useCallback(async () => {
    const exportData = table.getFilteredRowModel().rows.map(row => {
      const rowData: Record<string, unknown> = {}
      columns.forEach(col => {
        const cellValue = (row.original as Record<string, unknown>)[col.key as string]
        const formatted = col.format ? col.format(cellValue) : cellValue
        rowData[col.header] = formatted
      })
      return rowData
    })

    try {
      const XLSX = await import('xlsx')

      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(exportData)

      // Auto-size columns
      const colWidths = columns.map(col => ({ wch: Math.max(col.header.length, 20) }))
      ws['!cols'] = colWidths

      XLSX.utils.book_append_sheet(wb, ws, 'Data')
      XLSX.writeFile(wb, `${exportOptions.filename || 'table-export'}.xlsx`)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to export table to Excel', error)
    }
  }, [table, columns, exportOptions.filename])

  const exportToPDF = useCallback(async () => {
    try {
      const jsPDF = (await import('jspdf')).default
      const doc = new jsPDF()

      // Add title
      doc.setFontSize(16)
      doc.text(exportOptions.title || title || 'Data Export', 20, 20)

      // Add subtitle with date
      doc.setFontSize(10)
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 30)

      // Basic table export using text positioning
      doc.setFontSize(8)
      let yPosition = 45
      const pageHeight = 280
      const lineHeight = 6

      // Headers
      doc.setFont('helvetica', 'bold')
      const headerTexts = columns.map(col => col.header)
      const colWidth = 180 / headerTexts.length // Distribute width evenly

      headerTexts.forEach((header, index) => {
        doc.text(header, 20 + (index * colWidth), yPosition)
      })

      yPosition += lineHeight + 2
      doc.setFont('helvetica', 'normal')

      // Draw header underline
      doc.line(20, yPosition - 2, 200, yPosition - 2)

      // Data rows
      const rows = table.getFilteredRowModel().rows
      const maxRows = Math.min(rows.length, 35) // Limit rows to fit on page

      for (let i = 0; i < maxRows; i++) {
        const row = rows[i]
        const rowData = columns.map(col => {
          const cellValue = (row.original as Record<string, unknown>)[col.key as string]
          const formatted = col.format ? col.format(cellValue) : (cellValue?.toString() || '')
          return formatted.length > 15 ? formatted.substring(0, 12) + '...' : formatted
        })

        rowData.forEach((cellText, colIndex) => {
          doc.text(String(cellText), 20 + (colIndex * colWidth), yPosition)
        })

        yPosition += lineHeight

        // Check if we need a new page
        if (yPosition > pageHeight && i < maxRows - 1) {
          doc.addPage()
          yPosition = 20

          // Re-add headers on new page
          doc.setFont('helvetica', 'bold')
          headerTexts.forEach((header, index) => {
            doc.text(header, 20 + (index * colWidth), yPosition)
          })
          yPosition += lineHeight + 2
          doc.setFont('helvetica', 'normal')
          doc.line(20, yPosition - 2, 200, yPosition - 2)
        }
      }

      // Add footer with row count
      if (rows.length > maxRows) {
        doc.setFontSize(8)
        doc.text(
          `Showing ${maxRows} of ${rows.length} rows. Use Excel export for complete data.`,
          20,
          yPosition + 10
        )
      }

      doc.save(`${exportOptions.filename || 'table-export'}.pdf`)

    } catch (error) {
      console.error('Error exporting PDF:', error)
      alert('PDF export failed. Please try Excel export instead.')
    }
  }, [table, columns, exportOptions, title])  // Get frozen columns only (we don't need scrollableColumns variable)
  const frozenColumns = tableColumns.slice(0, frozenColumnsCount + (enableRowSelection ? 1 : 0))


  return (
    <div className={cn('w-full', className)}>
        {(title || description) && (
          <div className="space-y-1 pb-4">
            {title && <h2 className="text-lg font-semibold">{title}</h2>}
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
          </div>
        )}
        {/* Toolbar */}
        <div className="flex items-center justify-between flex-wrap gap-2 pb-2">
          <div className="flex items-center space-x-2">
            {/* Global Search */}
            {enableGlobalFilter && (
              <div className="relative w-full md:w-64">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={searchPlaceholder}
                  value={currentGlobalFilter}
                  onChange={(e) => handleGlobalFilterChange(e.target.value)}
                  className="pl-8 w-fit"
                />
                {currentGlobalFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1 h-6 w-6 p-0"
                    onClick={() => handleGlobalFilterChange('')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}

            {/* Row selection info */}
            {enableRowSelection && table.getFilteredSelectedRowModel().rows.length > 0 && (
              <Badge variant="secondary">
                {table.getFilteredSelectedRowModel().rows.length} row(s) selected
              </Badge>
            )}
          </div>


          <div className="flex items-center space-x-2">
            {customButtons}
          </div>

          <div className="flex items-center space-x-2 ">
            {/* Column Visibility */}
            {enableColumnVisibility && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Columns className="mr-2 h-4 w-4" />
                    Columns
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      >
                        {typeof (column.columnDef as { header?: string }).header === 'string'
                          ? (column.columnDef as { header: string }).header
                          : column.id}
                      </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Export Options */}
            {enableExport && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Download className="mr-2 h-4 w-4" />
                    Export
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportToExcel}>
                    <FileText className="mr-2 h-4 w-4" />
                    Export as Excel
                  </DropdownMenuItem>
                  {enablePDFExport && (
                    <DropdownMenuItem onClick={exportToPDF}>
                      <FileText className="mr-2 h-4 w-4" />
                      Export as PDF
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="relative">
          <div className="rounded-md border overflow-hidden">
            {frozenColumnsCount > 0 ? (
              // Table with frozen columns
              <div className="flex">
                {/* Frozen columns */}
                {frozenColumns.length > 0 && (
                  <div className="border-r bg-muted/30">
                    <Table>
                      <TableHeader>
                        {table.getHeaderGroups().map(headerGroup => (
                          <TableRow key={headerGroup.id} className="h-12">
                            {headerGroup.headers
                              .filter((_, index) => index < frozenColumns.length)
                              .map((header) => (
                                <TableHead
                                  key={header.id}
                                  style={{
                                    width: header.getSize() !== 150 ? header.getSize() : 'auto',
                                    minWidth: header.getSize() !== 150 ? header.getSize() : 'auto'
                                  }}
                                  className="bg-muted/50 h-12 py-1"
                                >
                                  {header.isPlaceholder
                                    ? null
                                    : flexRender(header.column.columnDef.header, header.getContext())
                                  }
                                </TableHead>
                              ))}
                          </TableRow>
                        ))}
                      </TableHeader>
                      <TableBody>
                        {!loading && table.getRowModel().rows?.length ? (
                          table.getRowModel().rows.map(row => (
                            <TableRow
                              key={row.id}
                              data-state={row.getIsSelected() && 'selected'}
                              className={cn(
                                'h-12', // Fixed height for alignment
                                onRowClick ? 'cursor-pointer' : ''
                              )}
                              onClick={() => onRowClick?.(row.original)}
                            >
                              {row.getVisibleCells()
                                .filter((_, index) => index < frozenColumns.length)
                                .map(cell => (
                                  <TableCell
                                    key={cell.id}
                                    style={{
                                      width: cell.column.getSize() !== 150 ? cell.column.getSize() : 'auto',
                                      minWidth: cell.column.getSize() !== 150 ? cell.column.getSize() : 'auto'
                                    }}
                                    className="bg-muted/30 h-12 py-1 align-middle"
                                  >
                                    <div className="flex items-center h-full">
                                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </div>
                                  </TableCell>
                                ))}
                            </TableRow>
                          ))
                        ) : loading ? (
                          <TableRow>
                            <TableCell colSpan={frozenColumns.length} className="h-24 text-center">
                              Loading...
                            </TableCell>
                          </TableRow>
                        ): hasNoRows ? (
                          <TableRow>
                            <TableCell colSpan={frozenColumns.length} className="h-24 text-center text-muted-foreground">
                              {emptyMessage}
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Scrollable columns */}
                {!loading && <div className="flex-1 overflow-x-auto">
                  <Table className="min-h-full">
                    <TableHeader>
                      {table.getHeaderGroups().map(headerGroup => (
                        <TableRow key={headerGroup.id} className="h-12">
                          {headerGroup.headers
                            .filter((_, index) => index >= frozenColumns.length)
                            .map((header) => (
                              <TableHead
                                key={header.id}
                                style={{
                                  width: header.getSize() !== 150 ? header.getSize() : 'auto',
                                  minWidth: header.getSize() !== 150 ? header.getSize() : 'auto'
                                }}
                                className="h-12 py-1"
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(header.column.columnDef.header, header.getContext())
                                }
                              </TableHead>
                            ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody>
                      {!loading && table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map(row => (
                          <TableRow
                            key={row.id}
                            data-state={row.getIsSelected() && 'selected'}
                            className={cn(
                              'h-12', // Fixed height for alignment
                              onRowClick ? 'cursor-pointer' : ''
                            )}
                            onClick={() => onRowClick?.(row.original)}
                          >
                            {row.getVisibleCells()
                              .filter((_, index) => index >= frozenColumns.length)
                              .map(cell => (
                                <TableCell
                                  key={cell.id}
                                  style={{
                                    width: cell.column.getSize() !== 150 ? cell.column.getSize() : 'auto',
                                    minWidth: cell.column.getSize() !== 150 ? cell.column.getSize() : 'auto'
                                  }}
                                  className="h-12 py-1 align-middle"
                                >
                                  <div className="flex items-center h-full">
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                  </div>
                                </TableCell>
                              ))}
                          </TableRow>
                        ))
                      ) : loading ? (
                        <TableRow>
                          <TableCell colSpan={frozenColumns.length} className="h-24 text-center">
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : hasNoRows ? (
                        <TableRow>
                          <TableCell colSpan={tableColumns.length - frozenColumns.length} className="h-24 text-center text-muted-foreground">
                            {emptyMessage}
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </div>}
              </div>
            ) : (
              // Regular table without frozen columns
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map(headerGroup => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead
                            key={header.id}
                            style={{
                              width: header.getSize() !== 150 ? header.getSize() : 'auto',
                              minWidth: header.getSize() !== 150 ? header.getSize() : 'auto'
                            }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())
                            }
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {!loading && table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map(row => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && 'selected'}
                          className={onRowClick ? 'cursor-pointer' : ''}
                          onClick={() => onRowClick?.(row.original)}
                        >
                          {row.getVisibleCells().map(cell => (
                            <TableCell
                              key={cell.id}
                              style={{
                                width: cell.column.getSize() !== 150 ? cell.column.getSize() : 'auto',
                                minWidth: cell.column.getSize() !== 150 ? cell.column.getSize() : 'auto'
                              }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : loading ? (
                      <TableRow>
                        <TableCell colSpan={tableColumns.length} className="h-24 text-center">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : hasNoRows ? (
                      <TableRow>
                        <TableCell colSpan={tableColumns.length} className="h-24 text-center text-muted-foreground">
                          {emptyMessage}
                        </TableCell>
                      </TableRow>
                    ): null}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {enablePagination && (
          <div className="flex items-center justify-between space-x-2 py-4">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium">Rows per page</p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 w-16">
                    {table.getState().pagination.pageSize}
                    <ChevronDown className="ml-1 h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {pageSizeOptions.map(size => (
                    <DropdownMenuItem
                      key={size}
                      onClick={() => {
                        table.setPageSize(size)
                        if (paginationCallbacks?.onPageSizeChange) {
                          paginationCallbacks.onPageSizeChange(size)
                        }
                      }}
                      className={table.getState().pagination.pageSize === size ?
                        'bg-accent text-accent-foreground' : ''}
                    >
                      {size}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center space-x-6 lg:space-x-8">
              <div className="flex w-24 items-center justify-center text-sm font-medium">
                Page {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    table.setPageIndex(0)
                    if (paginationCallbacks?.onPageChange) {
                      paginationCallbacks.onPageChange(0, table.getState().pagination.pageSize)
                    }
                  }}
                  disabled={!table.getCanPreviousPage()}
                >
                  {'<<'}
                </Button>
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    table.previousPage()
                    if (paginationCallbacks?.onPageChange) {
                      paginationCallbacks.onPageChange(
                        table.getState().pagination.pageIndex - 1,
                        table.getState().pagination.pageSize
                      )
                    }
                  }}
                  disabled={!table.getCanPreviousPage()}
                >
                  {'<'}
                </Button>
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    table.nextPage()
                    if (paginationCallbacks?.onPageChange) {
                      paginationCallbacks.onPageChange(
                        table.getState().pagination.pageIndex + 1,
                        table.getState().pagination.pageSize
                      )
                    }
                  }}
                  disabled={!table.getCanNextPage()}
                >
                  {'>'}
                </Button>
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    const lastPageIndex = table.getPageCount() - 1
                    table.setPageIndex(lastPageIndex)
                    if (paginationCallbacks?.onPageChange) {
                      paginationCallbacks.onPageChange(lastPageIndex, table.getState().pagination.pageSize)
                    }
                  }}
                  disabled={!table.getCanNextPage()}
                >
                  {'>>'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Info */}
        <div className="flex items-center justify-between space-x-2 text-sm text-muted-foreground">
          <div>
            {paginationCallbacks?.manualPagination ? (
              `Showing ${pagination.pageIndex * pagination.pageSize + 1} to ${Math.min(
                (pagination.pageIndex + 1) * pagination.pageSize,
                paginationCallbacks.totalCount || 0
              )} of ${paginationCallbacks.totalCount || 0} entries`
            ) : (
              <>
                Showing {table.getRowModel().rows.length} of {table.getFilteredRowModel().rows.length} entries
                {table.getFilteredRowModel().rows.length !== data.length &&
                  ` (filtered from ${data.length} total entries)`
                }
              </>
            )}
          </div>
          {enableRowSelection && (
            <div>
              {table.getFilteredSelectedRowModel().rows.length} of{' '}
              {paginationCallbacks?.manualPagination ?
                paginationCallbacks.totalCount || 0 :
                table.getFilteredRowModel().rows.length
              } row(s) selected
            </div>
          )}
        </div>
    </div>
  )
}
